import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { formatJsonValue } from "../../lib/format";
import { canonicalWorkingScopeSearch } from "../../lib/workingScope";
import { Code, DataTable, RouterLink, Text } from "../../ui";
import { SUPPLIER_LABELS } from "../simulations/launchDiagnosis";

type ResolvedVariable = components["schemas"]["ResolvedVariableResponse"];
type VariableSupplier = components["schemas"]["VariableSupplier"];

export interface ResolvedCellSupplierMap {
  persona?: { id: string; name: string };
  scenario?: { id: string; name: string };
}

interface SupplierOption {
  id: string;
  label: string;
}

interface ResolvedCellVariableRow extends ResolvedVariable {
  key: string;
}

export interface ResolvedCellViewProps {
  supplierLinkTarget?: "_blank";
  suppliers?: ResolvedCellSupplierMap;
  variables: Record<string, ResolvedVariable>;
}

export function resolvedCellSuppliers(
  cell: { persona_id: string | null; scenario_id: string | null },
  personaOptions: readonly SupplierOption[],
  scenarioOptions: readonly SupplierOption[],
): ResolvedCellSupplierMap | undefined {
  const persona = personaOptions.find(
    (option) => option.id === cell.persona_id,
  );
  const scenario = scenarioOptions.find(
    (option) => option.id === cell.scenario_id,
  );
  if (persona === undefined && scenario === undefined) {
    return undefined;
  }
  return {
    ...(persona === undefined
      ? {}
      : { persona: { id: persona.id, name: persona.label } }),
    ...(scenario === undefined
      ? {}
      : { scenario: { id: scenario.id, name: scenario.label } }),
  };
}

function supplierLabel(
  suppliedBy: VariableSupplier,
  suppliers?: ResolvedCellSupplierMap,
  target?: "_blank",
) {
  const persona = suppliers?.persona;
  if (suppliedBy === "persona" && persona !== undefined) {
    return (
      <>
        {SUPPLIER_LABELS.persona} ·{" "}
        <RouterLink
          rel={target ? "noopener" : undefined}
          target={target}
          search={(previous) => ({
            ...canonicalWorkingScopeSearch(previous),
            persona: persona.id,
          })}
          to="/personas"
        >
          {persona.name}
        </RouterLink>
      </>
    );
  }
  const scenario = suppliers?.scenario;
  if (suppliedBy === "scenario" && scenario !== undefined) {
    return (
      <>
        {SUPPLIER_LABELS.scenario} ·{" "}
        <RouterLink
          rel={target ? "noopener" : undefined}
          target={target}
          search={(previous) => ({
            ...canonicalWorkingScopeSearch(previous),
            scenario: scenario.id,
          })}
          to="/scenarios"
        >
          {scenario.name}
        </RouterLink>
      </>
    );
  }
  return SUPPLIER_LABELS[suppliedBy];
}

function variableColumns(
  suppliers?: ResolvedCellSupplierMap,
  supplierLinkTarget?: "_blank",
): Array<ColumnDef<ResolvedCellVariableRow>> {
  return [
    {
      accessorKey: "key",
      header: "Variable",
      cell: ({ row }) => <Code>{row.original.key}</Code>,
    },
    {
      header: "Value",
      id: "value",
      cell: ({ row }) => {
        if (row.original.is_sensitive) {
          return (
            <Text as="span" variant="muted">
              Sensitive — hidden
            </Text>
          );
        }
        const value = formatJsonValue(row.original.value);
        return <Code>{value}</Code>;
      },
    },
    {
      header: "Supplied by",
      id: "supplied_by",
      cell: ({ row }) =>
        supplierLabel(row.original.supplied_by, suppliers, supplierLinkTarget),
    },
  ];
}

export function ResolvedCellView({
  supplierLinkTarget,
  suppliers,
  variables,
}: ResolvedCellViewProps) {
  const rows = Object.entries(variables).map(([key, variable]) => ({
    key,
    ...variable,
  }));
  return (
    <DataTable
      ariaLabel="Resolved variables"
      columns={variableColumns(suppliers, supplierLinkTarget)}
      data={rows}
      getRowId={(row) => row.key}
    />
  );
}
